import { postToServer } from "@/client/utils/postToServer";
import { createSignalWithValue } from "@/client/utils/signal";
import { kSpliceProfileEndpoint } from "@/shared/splice-profile.api";
import { clearAuthToken } from "./auth-token.signal";

/**
 * Who is signed in on the website. The Auth0 ID token gives a name and an
 * email straight away; the Splice username and the avatar from splice.com
 * arrive a moment later from the gateway, through the server. Undefined until
 * the web login resolves, and always undefined in the plugin surfaces, where
 * the host owns the account and the page never sees it.
 */
export interface WebAccount {
    readonly username?: string;
    readonly name?: string;
    readonly nickname?: string;
    readonly email?: string;
    readonly picture?: string;
}

export const webAccountSignal = createSignalWithValue<WebAccount | undefined>(undefined);

export const setWebAccount = (account: WebAccount | undefined): void => {
    webAccountSignal.notify(account);
};

/**
 * Asks the server for the Splice profile and lays it over what Auth0 gave:
 * the username and splice.com avatar win, the rest fills gaps. A failure
 * leaves the Auth0 account in place rather than clearing it.
 */
export const refreshSpliceProfile = async (): Promise<void> => {
    const result = await postToServer(kSpliceProfileEndpoint, {}).catch(
        (error: unknown): Error => (error instanceof Error ? error : new Error(String(error))),
    );
    if (result instanceof Error) {
        return;
    }
    const current = webAccountSignal.value;
    if (current === undefined) {
        return;
    }
    const { profile } = result;
    webAccountSignal.notify({
        ...current,
        username: profile.username,
        name: profile.name ?? current.name,
        email: profile.email ?? current.email,
        picture: profile.avatarUrl ?? current.picture,
    });
};

let logoutFn: (() => Promise<void>) | undefined;

/** Registered by the web auth service once its Auth0 client exists. */
export const registerWebLogout = (fn: () => Promise<void>): void => {
    logoutFn = fn;
};

/**
 * Signs the website out. The token is dropped first so nothing in flight
 * keeps using it; the Auth0 logout then leaves the page, so the account
 * signal needs no update of its own.
 */
export const logoutWeb = async (): Promise<void> => {
    clearAuthToken();
    setWebAccount(undefined);
    await logoutFn?.();
};

/** The line the menu shows for the account: the Splice username, else a name, else a handle, else the email. */
export const getAccountDisplayName = (account: Readonly<WebAccount>): string =>
    firstNonEmpty([account.username, account.name, account.nickname, account.email]) ?? "Signed in";

/**
 * One or two letters for the avatar when there is no picture: the initials of
 * a two-word name, else the first letter of whatever the display line is.
 */
export const getAccountInitials = (account: Readonly<WebAccount>): string => {
    const display = getAccountDisplayName(account);
    const words = display.split(/\s+/).filter((word) => word !== "");
    if (words.length >= 2) {
        return (words[0][0] + words[words.length - 1][0]).toUpperCase();
    }
    return display.slice(0, 1).toUpperCase();
};

/** The email reads as a second line only when the first line is not already it. */
export const getAccountSecondaryLine = (account: Readonly<WebAccount>): string | undefined =>
    account.email !== undefined && account.email !== getAccountDisplayName(account)
        ? account.email
        : undefined;

const firstNonEmpty = (values: readonly (string | undefined)[]): string | undefined =>
    values.find((value) => value !== undefined && value.trim() !== "");
